"use client";

import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
  DragEndEvent
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../trip/SortableItem";
import { Stops } from "../../types/trip.types";
import { Coordinates } from "../../types/distance.types";
import { Plus } from "lucide-react";

export interface TripPlannerProps {
  stops: Stops;
  hoveredStopIndex?: number | null;
  isDraggingMarker?: boolean;
  tripLoading?: boolean;
  onStopChange: (index: number, address: string, coords?: Coordinates) => void;
  addStop: () => void;
  removeStop: (index: number) => void;
  clearStop: (index: number) => void;
  reorderStops: (oldIndex: number, newIndex: number) => void;
}

export default function TripPlanner({
  stops,
  hoveredStopIndex,
  isDraggingMarker = false,
  tripLoading = false,
  onStopChange,
  addStop,
  removeStop,
  clearStop,
  reorderStops,
}: TripPlannerProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 10 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = parseInt(active.id as string, 10);
    const newIndex = parseInt(over.id as string, 10);
    reorderStops(oldIndex, newIndex);
  };

  return (
    <>
      <div className="input-column" style={{ opacity: tripLoading ? 0.5 : 1, pointerEvents: tripLoading ? "none" : "all" }}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={stops.map((_, i) => i.toString())}
            strategy={verticalListSortingStrategy}
          >
            {stops.map((stop, i) => {
              let placeholder = "";
              if (i === 0) placeholder = "Start Address";
              else if (i === stops.length - 1) placeholder = "Final Address";
              else placeholder = `Stop ${i}`;

              const shouldDim =
                hoveredStopIndex !== null &&
                hoveredStopIndex !== i &&
                (isDraggingMarker || hoveredStopIndex !== null);

              return (
                <SortableItem
                  key={i}
                  id={i.toString()}
                  value={stop.address}
                  placeholder={placeholder}
                  onChange={(val, coords) => onStopChange(i, val, coords)}
                  onRemove={() => removeStop(i)}
                  onClear={() => clearStop(i)}
                  isDimmed={shouldDim || tripLoading}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>

      <div className="planner-actions" style={{ opacity: tripLoading ? 0.5 : 1, pointerEvents: tripLoading ? "none" : "all" }}>
        <button 
          onClick={addStop} 
          className="route-button button"
          data-testid="add-stop-button"
        >
          <Plus size={20} className="lucide-icon"/>
        </button>
      </div>
    </>
  );
}
